/**
 * Help Content API Module
 *
 * Provides methods for fetching contextual help content
 */

import api from './client';

// Types
export interface PageHelp {
  id: number;
  page_key: string;
  title: string;
  description: string;
  key_features: Array<{
    title: string;
    description: string;
  }>;
  tips: string[];
  sections: Array<{
    id: string;
    title: string;
    description: string;
    items?: Array<Record<string, string>>;
  }>;
  platform: 'ALL' | 'SUPPLIER' | 'CUSTOMER';
  related_pages: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FieldHelp {
  id: number;
  field_key: string;
  label: string;
  description: string;
  formula: string;
  example: string;
  related_fields: string[];
  learn_more_url: string;
  platform: 'ALL' | 'SUPPLIER' | 'CUSTOMER';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GlossaryTerm {
  id: number;
  term: string;
  abbreviation: string;
  definition: string;
  category: string;
  related_terms: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HelpSearchResult {
  type: 'page' | 'field' | 'glossary';
  id: number;
  title: string;
  description: string;
  key: string;
  match_context?: string;
}

export interface HelpSearchResponse {
  query: string;
  count: number;
  results: HelpSearchResult[];
}

export interface HelpStats {
  pages: number;
  fields: number;
  glossary_terms: number;
  categories: string[];
}

// API Methods
export const helpAPI = {
  // Page Help
  getPageHelp: (pageKey: string) =>
    api.get<PageHelp>(`/help/pages/${pageKey}/`),

  getAllPageHelp: () =>
    api.get<PageHelp[]>('/help/pages/'),

  getPageHelpByPlatform: (platform: string) =>
    api.get<PageHelp[]>('/help/pages/by_platform/', { params: { platform } }),

  // Field Help
  getFieldHelp: (fieldKey: string) =>
    api.get<FieldHelp>(`/help/fields/${fieldKey}/`),

  getAllFieldHelp: () =>
    api.get<FieldHelp[]>('/help/fields/'),

  getFieldHelpByPage: (pageKey: string) =>
    api.get<FieldHelp[]>('/help/fields/by_page/', { params: { page: pageKey } }),

  bulkGetFieldHelp: (fieldKeys: string[]) =>
    api.post<FieldHelp[]>('/help/fields/bulk_get/', { field_keys: fieldKeys }),

  // Glossary
  getGlossaryTerm: (id: number) =>
    api.get<GlossaryTerm>(`/help/glossary/${id}/`),

  getAllGlossaryTerms: () =>
    api.get<GlossaryTerm[]>('/help/glossary/'),

  getGlossaryByCategory: () =>
    api.get<Record<string, GlossaryTerm[]>>('/help/glossary/by_category/'),

  lookupGlossaryTerm: (query: string) =>
    api.get<GlossaryTerm>('/help/glossary/lookup/', { params: { q: query } }),

  // Search
  searchHelp: (query: string, limit = 20) =>
    api.get<HelpSearchResponse>('/help/search/', { params: { q: query, limit } }),

  // Stats
  getHelpStats: () =>
    api.get<HelpStats>('/help/stats/'),
};
